/**
 * RNC Construct Projects table — shown on the Signals page RNC tab.
 * Loads live rows from `bnc.projects` via `/api/rmc/projects`.
 */
import { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { FilterSelect } from "@/components/common/FilterSelect";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { signalsApi } from "@/lib/api";

const COLUMNS = [
  { key: "project_name", label: "Project", sortable: true },
  { key: "city", label: "City", sortable: true },
  { key: "country", label: "Country", sortable: true },
  { key: "sector", label: "Sector", sortable: true },
  { key: "stage", label: "Stage", sortable: true },
  { key: "est_project_value", label: "Value", sortable: true },
];

const PAGE_SIZES = [5, 10, 25];

function fmt(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

export default function RmcProjectsTable({ refreshToken = 0 }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [query, setQuery] = useState("");
  const [draftQuery, setDraftQuery] = useState("");
  const [sort, setSort] = useState("project_name");
  const [order, setOrder] = useState("asc");
  const [state, setState] = useState({ loading: true, data: null, error: null });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await signalsApi.listRmcProjects({
        limit: pageSize,
        offset: (page - 1) * pageSize,
        q: query || undefined,
        sort,
        order,
      });
      setState({ loading: false, data, error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: e.message || "Failed to load" });
    }
  }, [page, pageSize, query, sort, order]);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  function submitSearch(e) {
    e?.preventDefault?.();
    setPage(1);
    setQuery(draftQuery.trim());
  }

  function changePageSize(size) {
    setPageSize(size);
    setPage(1);
  }

  function toggleSort(key) {
    if (sort === key) {
      setOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSort(key);
      setOrder("asc");
    }
    setPage(1);
  }

  function getSortIcon(colKey) {
    if (sort !== colKey) return "";
    return order === "asc" ? " ↑" : " ↓";
  }

  const projects = state.data?.projects || [];
  const total = state.data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  if (state.loading && !state.data) {
    return (
      <div className="space-y-2" data-testid="rnc-projects-loading">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!state.loading && state.error && !state.data) {
    return (
      <div
        className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700"
        data-testid="rnc-projects-error"
      >
        {state.error}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="rnc-projects-panel">
      <form
        onSubmit={submitSearch}
        className="flex items-center gap-2 flex-wrap"
        data-testid="rnc-projects-search"
      >
        <div className="relative flex-1 min-w-[200px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={draftQuery}
            onChange={(e) => setDraftQuery(e.target.value)}
            placeholder="Search projects…"
            className="h-9 w-full rounded-md border border-slate-200 bg-white pl-8 pr-3 text-[12.5px] outline-none focus:ring-2 focus:ring-indigo-100"
            data-testid="rnc-projects-search-input"
          />
        </div>
        <Button type="submit" size="sm" className="h-9" data-testid="rnc-projects-search-btn">
          Search
        </Button>
      </form>

      <div
        className="rounded-lg border border-slate-200 overflow-hidden bg-white"
        data-testid="rnc-projects-table"
      >
        {!projects.length ? (
          <div
            className="px-5 py-12 text-center text-[13px] text-slate-500"
            data-testid="rnc-projects-empty"
          >
            {query ? "No projects match your search." : "No projects found."}
          </div>
        ) : (
          <table className="w-full text-[12.5px]">
            <thead className="bg-slate-50">
              <tr className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
                {COLUMNS.map((col) => (
                  <th key={col.key} className="px-3 py-2 whitespace-nowrap">
                    {col.sortable ? (
                      <button
                        type="button"
                        className={cn(
                          "inline-flex items-center gap-1 hover:text-slate-800",
                          sort === col.key && "text-indigo-700",
                        )}
                        onClick={() => toggleSort(col.key)}
                        data-testid={`rnc-sort-${col.key}`}
                      >
                        {col.label}
                        {getSortIcon(col.key)}
                      </button>
                    ) : (
                      col.label
                    )}
                  </th>
                ))}
                <th className="px-3 py-2 whitespace-nowrap">Action</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr
                  key={p.project_id}
                  className="border-t border-slate-100 hover:bg-indigo-50/30"
                  data-testid={`rnc-project-row-${p.project_id}`}
                >
                  {COLUMNS.map((col) => (
                    <td
                      key={col.key}
                      className={cn(
                        "px-3 py-2.5 align-top",
                        col.key === "project_name"
                          ? "font-medium text-slate-900"
                          : "text-slate-700 whitespace-nowrap",
                      )}
                    >
                      {fmt(p[col.key])}
                    </td>
                  ))}
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-[11px]"
                      onClick={() => navigate(`/signals/rnc/${encodeURIComponent(p.project_id)}`)}
                      data-testid={`view-rnc-${p.project_id}`}
                    >
                      View Details
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div
          className="border-t border-slate-100 bg-slate-50/60 px-3 py-2 flex items-center justify-between gap-3 flex-wrap text-[11px] text-slate-500"
          data-testid="rnc-projects-pagination"
        >
          <div className="tabular-nums">
            {total === 0 ? "0 projects" : `${from}–${to} of ${total}`}
            {state.loading && <span className="ml-2 text-slate-400">Updating…</span>}
          </div>

          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1.5">
              <span>Rows</span>
              <FilterSelect
                variant="field"
                value={pageSize}
                onChange={(v) => changePageSize(Number(v))}
                options={PAGE_SIZES.map((size) => ({ value: size, label: String(size) }))}
                className="w-auto"
                triggerClassName="h-7 w-auto min-w-[52px] px-1.5 text-[11px]"
                data-testid="rnc-page-size"
                aria-label="Rows per page"
              />
            </label>

            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 px-2"
              disabled={page <= 1 || state.loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="rnc-page-prev"
            >
              <ChevronLeft size={12} />
            </Button>
            <span className="tabular-nums min-w-[4.5rem] text-center">
              Page {Math.min(page, totalPages)} / {totalPages}
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 px-2"
              disabled={page >= totalPages || state.loading}
              onClick={() => setPage((p) => p + 1)}
              data-testid="rnc-page-next"
            >
              <ChevronRight size={12} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

RmcProjectsTable.propTypes = {
  refreshToken: PropTypes.number,
};
